/**
 * FooterRevealController
 */
if (!window.FooterRevealController) {
  class FooterRevealController {
    static selectors = {
      footer: '.js-section-footer',
      mainContent: '#MainContent'
    };

    static classes = {
      isVisible: 'is-visible'
    };

    constructor() {
      this.selectors = FooterRevealController.selectors;
      this.classes = FooterRevealController.classes;
      this.footer = null;
      this.mainContent = null;
      this.abortController = new AbortController();

      this.init();
    }

    init() {
      this.footer = document.querySelector(this.selectors.footer);
      this.mainContent = document.querySelector(this.selectors.mainContent);

      if (!this.footer) return;

      this.updateFooterHeight();
      this.bindEvents();
      this.checkVisibility();

      if (document.body.hasAttribute('data-fullpage-scroll')) {
        this._syncFromSwiper();
      }
    }

    updateFooterHeight() {
      if (!this.footer) return;
      const footerEl = this.footer.querySelector('.ft-Footer') || this.footer;
      const height = footerEl.offsetHeight || this.footer.offsetHeight || 0;
      if (height > 0) {
        document.documentElement.style.setProperty('--footer-height', `${Math.round(height)}px`);
        document.documentElement.setAttribute('data-footer-height', `${Math.round(height)}px`);
      }
    }

    _syncFromSwiper() {
      const isPDP = document.body.classList.contains('template-product');
      if (isPDP && !document.body.classList.contains('is-editorial-active')) {
        this.setVisible(false);
        return;
      }

      if (window.fullpageScrollInstance?.swiper) {
        this.setVisible(window.fullpageScrollInstance.isFooterVisible());
        return;
      }

      let attempts = 0;
      const maxAttempts = 50;
      const timer = setInterval(() => {
        attempts++;
        const isPDPLive = document.body.classList.contains('template-product');
        if (isPDPLive && !document.body.classList.contains('is-editorial-active')) {
          clearInterval(timer);
          this.setVisible(false);
          return;
        }

        if (window.fullpageScrollInstance?.swiper) {
          clearInterval(timer);
          this.setVisible(window.fullpageScrollInstance.isFooterVisible());
        } else if (attempts >= maxAttempts) {
          clearInterval(timer);
        }
      }, 100);
    }

    setVisible(isVisible) {
      if (!this.footer) return;
      this.footer.classList.toggle(this.classes.isVisible, isVisible);
      if (isVisible) {
        this.updateFooterHeight();
      }
    }

    checkVisibility() {
      if (!this.footer) return;

      // On fullpage pages, wait for fullpage:ready; once swiper is active, visibility is driven by fullpage:slideChange events
      if (document.body.hasAttribute('data-fullpage-scroll')) {
        const fullpage = window.fullpageScrollInstance;
        if (!fullpage?.isReady || fullpage.swiper) return;
      }

      if (this.mainContent) {
        const rect = this.mainContent.getBoundingClientRect();
        const isAtBottom = rect.bottom <= window.innerHeight + 60;
        this.setVisible(isAtBottom);
      } else {
        const scrollBottom = window.scrollY + window.innerHeight;
        const pageHeight = document.documentElement.scrollHeight;
        const isAtBottom = scrollBottom >= pageHeight - 60;
        this.setVisible(isAtBottom);
      }
    }

    bindEvents() {
      const { signal } = this.abortController;

      window.addEventListener('scroll', () => this.checkVisibility(), { passive: true, signal });
      window.addEventListener('resize', () => {
        this.updateFooterHeight();
        this.checkVisibility();
      }, { passive: true, signal });
      window.addEventListener('load', () => {
        this.updateFooterHeight();
        this.checkVisibility();
      }, { passive: true, signal });

      document.addEventListener('fullpage:slideChange', (e) => {
        if (typeof e.detail?.isFooterVisible === 'boolean') {
          this.setVisible(e.detail.isFooterVisible);
        }
      }, { signal });

      document.addEventListener('fullpage:footerChange', (e) => {
        if (typeof e.detail?.isVisible === 'boolean') {
          this.setVisible(e.detail.isVisible);
        }
      }, { signal });

      document.addEventListener('fullpage:ready', (e) => {
        this.updateFooterHeight();
        if (!e.detail?.swiper) {
          this.checkVisibility();
          return;
        }
        this.setVisible(e.detail.instance.isFooterVisible(e.detail.swiper));
      }, { signal });

      this.bindFooterInteraction(signal);

      // Shopify Theme Editor Events
      const handleShopifySectionUpdate = () => {
        setTimeout(() => {
          this.footer = document.querySelector(this.selectors.footer);
          this.mainContent = document.querySelector(this.selectors.mainContent);
          this.updateFooterHeight();
          this.checkVisibility();
        }, 100);
      };

      document.addEventListener('shopify:section:load', handleShopifySectionUpdate, { signal });
      document.addEventListener('shopify:section:reorder', handleShopifySectionUpdate, { signal });
      document.addEventListener('shopify:section:select', handleShopifySectionUpdate, { signal });
    }

    bindFooterInteraction(signal) {
      if (!this.footer) return;

      let touchStartY = 0;
      let isTouchDown = false;

      // Where the footer is a step after the last slide, the fullpage controller handles wheel input.
      this.footer.addEventListener('wheel', (e) => {
        if (window.fullpageScrollInstance?.hasFooterStep?.()) return;
        if (!this.footer.classList.contains(this.classes.isVisible)) return;
        if (this.footer.scrollTop > 0) return;
        if (e.deltaY < -15) {
          if (e.cancelable) e.preventDefault();
          document.dispatchEvent(new CustomEvent('fullpage:prevSlide'));
        }
      }, { passive: false, signal });

      this.footer.addEventListener('touchstart', (e) => {
        if (e.touches && e.touches.length > 0) {
          touchStartY = e.touches[0].clientY;
          isTouchDown = true;
        }
      }, { passive: true, signal });

      this.footer.addEventListener('touchmove', (e) => {
        if (!isTouchDown || !e.touches || !e.touches.length) return;
        if (!this.footer.classList.contains(this.classes.isVisible)) return;
        if (this.footer.scrollTop > 0) return;

        const currentY = e.touches[0].clientY;
        const diffY = touchStartY - currentY;

        if (diffY < -30) {
          isTouchDown = false;
          document.dispatchEvent(new CustomEvent('fullpage:prevSlide'));
        }
      }, { passive: true, signal });

      this.footer.addEventListener('touchend', () => {
        isTouchDown = false;
      }, { passive: true, signal });
    }

    destroy() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = new AbortController();
      }
      document.documentElement.style.removeProperty('--footer-height');
      document.documentElement.removeAttribute('data-footer-height');
    }
  }

  window.FooterRevealController = FooterRevealController;

  const initFooterReveal = () => {
    if (!window.footerRevealInstance) {
      window.footerRevealInstance = new FooterRevealController();
    } else {
      window.footerRevealInstance.init();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFooterReveal);
  } else {
    initFooterReveal();
  }
}

/**
 * FooterNewsletterController
 * Submits the footer newsletter form to a Klaviyo list via the Client Subscriptions API.
 */
if (!window.FooterNewsletterController) {
  class FooterNewsletterController {
    static selectors = {
      newsletterForm: '[data-footer-newsletter-form]',
      newsletterStatus: '[data-footer-newsletter-status]',
      newsletterPopupOpen: '[data-newsletter-popup-open]',
      newsletterPopup: '[data-newsletter-popup]',
      newsletterPopupClose: '[data-newsletter-popup-close]'
    };

    constructor() {
      this.selectors = FooterNewsletterController.selectors;
      this.abortController = new AbortController();
      this.activePopup = null;
      this.previousFocus = null;

      this.bindEvents();
    }

    closePopup() {
      if (!this.activePopup) return;

      this.activePopup.classList.remove('is-open');
      this.activePopup.hidden = true;
      this.activePopup.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('is-newsletter-popup-open');

      const trigger = this.previousFocus;
      this.activePopup = null;
      this.previousFocus = null;

      trigger?.focus();
    }

    bindEvents() {
      const { signal } = this.abortController;

      document.addEventListener('click', (e) => {
        const openButton = e.target.closest(this.selectors.newsletterPopupOpen);

        if (openButton) {
          const popupId = openButton.getAttribute('aria-controls');
          const popup = popupId ? document.getElementById(popupId) : null;

          if (!popup) return;

          this.previousFocus = openButton;
          this.activePopup = popup;

          popup.hidden = false;
          popup.setAttribute('aria-hidden', 'false');
          document.body.classList.add('is-newsletter-popup-open');

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              popup.classList.add('is-open');
            });
          });

          popup.querySelector('.ft-NewsletterPopup_Dialog')?.focus();
          return;
        }

        const closeButton = e.target.closest(this.selectors.newsletterPopupClose);

        if (closeButton && this.activePopup?.contains(closeButton)) {
          this.closePopup();
        }
      }, { signal });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.activePopup) {
          this.closePopup();
        }
      }, { signal });

      document.addEventListener('submit', (e) => {
        const form = e.target.closest(this.selectors.newsletterForm);
        if (!form) return;

        e.preventDefault();
        this.submitNewsletter(form);
      }, { signal });
    }

    async submitNewsletter(form) {
      const status = form.querySelector(this.selectors.newsletterStatus);
      const submitButton = form.querySelector('[type="submit"]');
      const { klaviyoCompanyId, klaviyoListId, klaviyoSource, country } = form.dataset;

      const setStatus = (message) => {
        if (status) status.textContent = message;
      };

      if (!klaviyoCompanyId || !klaviyoListId) {
        console.warn('[Footer newsletter] Missing Klaviyo public key or list ID for this country.');
        setStatus(status?.dataset.errorMessage);
        return;
      }

      const properties = { newsletter_country: country };

      if (form.hasAttribute('data-newsletter-popup-form')) {
        const selectedInterest = form.querySelector(
          'input[name="newsletter_interest"]:checked'
        )?.value;

        if (!selectedInterest) {
          setStatus('Please select an interest.');
          return;
        }

        properties.newsletter_interest = selectedInterest;
      }

      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: klaviyoSource,
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: form.elements.email.value.trim(),
                  properties
                }
              }
            }
          },
          relationships: {
            list: { data: { type: 'list', id: klaviyoListId } }
          }
        }
      };

      if (submitButton) submitButton.disabled = true;

      try {
        const response = await fetch(
          `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(klaviyoCompanyId)}`,
          {
            method: 'POST',
            headers: {
              accept: 'application/vnd.api+json',
              'content-type': 'application/vnd.api+json',
              revision: '2026-07-15'
            },
            body: JSON.stringify(payload)
          }
        );

        if (!response.ok) throw new Error(`Klaviyo responded with ${response.status}`);

        form.reset();
        setStatus(status?.dataset.successMessage);
      } catch (error) {
        console.error('[Footer newsletter]', error);
        setStatus(status?.dataset.errorMessage);
      } finally {
        if (submitButton) submitButton.disabled = false;
      }
    }
  }

  window.FooterNewsletterController = FooterNewsletterController;

  if (!window.footerNewsletterInstance) {
    window.footerNewsletterInstance = new FooterNewsletterController();
  }
}
